import { type ReactNode, useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { useMe } from "@/queries/auth";
import { useAuthStore } from "@/stores/auth";

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const location = useRouterState({ select: (s) => s.location });
  const { data: me, isPending, isError, error } = useMe(Boolean(accessToken));

  useEffect(() => {
    if (!accessToken) {
      void navigate({ to: "/login", search: { next: location.pathname } });
      return;
    }
    if (isError) {
      void navigate({ to: "/login", search: { next: location.pathname } });
      return;
    }
    if (
      me?.must_change_password &&
      location.pathname !== "/force-change-password"
    ) {
      void navigate({ to: "/force-change-password" });
    }
  }, [accessToken, isError, me, location.pathname, navigate]);

  if (!accessToken) return null;
  if (isPending) return null;
  if (isError) {
    void error;
    return null;
  }
  if (me?.must_change_password && location.pathname !== "/force-change-password") {
    return null;
  }

  return <>{children}</>;
}
